import { ThemeProvider } from "@mui/material/styles";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import type { JSX } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { appEnvironment, ApplicationContext, Environment, fetchEnvironment } from "./ApplicationContext";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { useCurrentUser } from "./hooks";
import { AlertProvider } from "./providers/AlertProvider";
import SearchManagerProvider from "./providers/SearchManagerProvider";
import { refreshToken } from "./redux/authSlice";
import store from "./redux/store";
import SchemaRouter from "./SchemaRouter";
import WeaveBrandTheme from "./themes/WeaveBrandTheme";
import { beginRefreshToken } from "./utils/authentication";
import { cleanupExpiredCache, persistQueryCache, restoreQueryCache } from "./utils/reactQueryPersistence";

import "./SchemaPortalApp.css";
import "@hig/fonts/build/ArtifaktElement400.css";
import "@hig/fonts/build/ArtifaktElement600.css";
import "@hig/fonts/build/ArtifaktElement700.css";
import "@hig/fonts/build/ArtifaktElement800.css";

interface QueryErrorType {
    status?: number;
    error?: { errorCode?: string };
}

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error: unknown) => {
            const typedError = error as QueryErrorType;
            // Ensure 'error' is an instance of 'ExtendedErrorResponse' object.
            // Also, ensure that the 401 is due to expired token and not due to
            // "ACM-002: User or Client is not authorized for this operation."
            if (typedError) {
                if (typedError["status"] === 401) {
                    const innerError = typedError["error"];
                    if (innerError && innerError["errorCode"] === "AUTH-006") {
                        const { auth } = store.getState();
                        if (auth.isLogin && auth.refreshToken) {
                            beginRefreshToken(auth.refreshToken).then((tokenData) => {
                                tokenData && store.dispatch(refreshToken(tokenData));
                            });
                        }
                    }
                } else if (typedError["status"] === 431) {
                    // 431 Request Header Fields Too Large, this happens when there
                    // are too many cookies in the request. This is a known issue by
                    // our analytics code. Hooks like useOrganizations() will receive
                    // the error and display an alert to get user to clear cookies.
                }
            }
        },
    }),
});

function SchemaPortalAppInner(): JSX.Element {
    const { data: currentUser } = useCurrentUser();
    const userId = currentUser?.sub;

    React.useEffect(() => {
        // Clean up expired cache entries on mount
        cleanupExpiredCache();

        // Restore React Query cache from sessionStorage on mount
        restoreQueryCache(queryClient, userId);
    }, [userId]);

    // Persist cache periodically and on page unload
    React.useEffect(() => {
        // Persist cache every 1 minute
        const persistInterval = setInterval(() => {
            persistQueryCache(queryClient, userId);
        }, 60000);

        // Persist cache on page unload
        const handleBeforeUnload = (): void => {
            persistQueryCache(queryClient, userId);
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            clearInterval(persistInterval);
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [userId]);

    return (
        <SearchManagerProvider>
            <ThemeProvider theme={WeaveBrandTheme.light}>
                <div style={{ display: "none" }} id="svg-container"></div>
                <AlertProvider>
                    <ErrorBoundary>
                        <SchemaRouter />
                    </ErrorBoundary>
                </AlertProvider>
            </ThemeProvider>
        </SearchManagerProvider>
    );
}

function SchemaPortalAppContent(): JSX.Element {
    // Start with appEnvironment (which will be updated by fetchEnvironment)
    const [environment, setEnvironment] = React.useState<Environment | undefined>(appEnvironment);
    const [isEnvironmentReady, setIsEnvironmentReady] = React.useState(false);

    React.useEffect(() => {
        // Fetch environment immediately and update both state and appEnvironment
        // This must complete before authentication can happen
        fetchEnvironment().then((env: Environment) => {
            setEnvironment(env);
            setIsEnvironmentReady(true);
            // appEnvironment is already updated in fetchEnvironment()
        });

        // Perform a one-time downloading of icon resources that are required
        // by UI components such as <Select> (its 'down caret' part). See the
        // "How to consume icons in HTML/CSS" section in the following document:
        //
        //      https://pages.git.autodesk.com/dpe/digital-hig/#/ref-app/icons
        //
        fetch("/ui-controls.svg").then((response) => {
            if (response.ok) {
                response.text().then((contents) => {
                    const container = document.getElementById("svg-container");
                    if (container) {
                        container.innerHTML = contents;
                    }
                });
            }
        });
    }, []);

    // Use appEnvironment as fallback while waiting for environment to load
    // This ensures the app and header can render immediately
    const envToUse = environment ?? appEnvironment;

    // Don't render the app until environment is loaded to prevent authentication and API issues
    // Check if critical config values are present (auth, search, schema APIs)
    const hasAuthConfig = envToUse.REACT_APP_AUTH_BASEURL && envToUse.REACT_APP_CLIENT_ID;
    const hasApiConfig = envToUse.REACT_APP_SEARCH_BASEURL && envToUse.REACT_APP_SCHEMA_BASEURL_V3;
    const hasRequiredConfig = hasAuthConfig && hasApiConfig;
    if (!isEnvironmentReady && !hasRequiredConfig) {
        return <></>; // Show nothing while environment loads
    }

    return (
        <ApplicationContext.Provider value={{ env: envToUse, isEnvironmentReady }}>
            <ReduxProvider store={store}>
                <SchemaPortalAppInner />
            </ReduxProvider>
        </ApplicationContext.Provider>
    );
}

export default function SchemaPortalApp(): JSX.Element {
    return (
        <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen />
            <SchemaPortalAppContent />
        </QueryClientProvider>
    );
}
